import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle2, MailWarning } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import { getApiUrl } from '../../utils/authUtils';

const API = getApiUrl();

/**
 * Confirm Login Email
 *
 * Landing page for the verification link sent when an agency owner's
 * sign-in email change is requested. The old login stays valid until this
 * confirmation completes.
 */
const ConfirmLoginEmail = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';

  const [state, setState] = useState('checking'); // checking | ready | invalid | confirming | confirmed
  const [pendingEmail, setPendingEmail] = useState('');
  const [confirmedEmail, setConfirmedEmail] = useState('');
  const [statusDetail, setStatusDetail] = useState('');

  useEffect(() => {
    if (!token) {
      setState('invalid');
      setStatusDetail('This confirmation link is invalid or missing.');
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/auth/confirm-login-email/verify`, {
          params: { token },
        });
        if (cancelled) return;
        if (res.data?.valid) {
          setPendingEmail(res.data?.pending_email || '');
          setState('ready');
        } else {
          setState('invalid');
          setStatusDetail(
            res.data?.status === 'expired'
              ? 'This confirmation link has expired. Request the change again.'
              : 'This confirmation link is invalid or was already used.',
          );
        }
      } catch {
        if (!cancelled) {
          setState('invalid');
          setStatusDetail('This confirmation link is invalid or was already used.');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const handleConfirm = async () => {
    setState('confirming');
    try {
      const res = await axios.post(`${API}/auth/confirm-login-email`, { token });
      setConfirmedEmail(res.data?.email || pendingEmail);
      setState('confirmed');
      toast.success('Sign-in email updated');
    } catch (error) {
      const detail = error.response?.data?.detail;
      setState('invalid');
      setStatusDetail(typeof detail === 'string' ? detail : 'Confirmation failed. Request the change again.');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-slate-900">Confirm your new sign-in email</h1>
          <p className="text-slate-600 mt-2">
            Your current login keeps working until you confirm this change.
          </p>
        </div>

        <div className="bg-white rounded-2xl shadow-xl border border-slate-200 p-8 text-center space-y-4">
          {state === 'checking' && <p className="text-slate-600">Checking confirmation link…</p>}

          {state === 'invalid' && (
            <>
              <MailWarning className="mx-auto text-amber-500" size={40} />
              <p className="text-slate-700" data-testid="confirm-login-email-error">
                {statusDetail}
              </p>
            </>
          )}

          {(state === 'ready' || state === 'confirming') && (
            <>
              <p className="text-slate-700">
                Confirm changing your sign-in email to{' '}
                <strong data-testid="confirm-login-email-pending">{pendingEmail}</strong>?
              </p>
              <Button
                className="w-full bg-emerald-600 hover:bg-emerald-700"
                onClick={handleConfirm}
                disabled={state === 'confirming'}
                data-testid="confirm-login-email-button"
              >
                {state === 'confirming' ? 'Confirming…' : 'Confirm new sign-in email'}
              </Button>
            </>
          )}

          {state === 'confirmed' && (
            <>
              <CheckCircle2 className="mx-auto text-emerald-600" size={40} />
              <p className="text-slate-700" data-testid="confirm-login-email-success">
                Your sign-in email is now <strong>{confirmedEmail}</strong>. Use it the next
                time you log in.
              </p>
            </>
          )}

          <div className="pt-2">
            <Link to="/login" className="text-sm text-emerald-600 hover:text-emerald-700">
              Back to sign in
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConfirmLoginEmail;
